import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { Alert, Button, Card, PageContainer, TextField } from '@shared';

import { getConfig } from '@/config/env';
import { useTranslations } from '@/i18n/client';
import { createPortalApi } from '@/services/api';
import type { AuthApi, PasswordResetMailTemplate } from '@/services/api/auth-api';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ForgotPasswordFormValues {
  email: string;
}

export interface ForgotPasswordPageProps {
  /** Test-only injection point for the network boundary. */
  auth?: AuthApi;
}

function buildResetLink(email: string): string {
  const { basePath } = getConfig();
  const pathname = basePath === '/' ? '/reset-password' : `${basePath}/reset-password`;
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set('email', email);
  return `${url.toString()}&token={Token}`;
}

function buildMailTemplate(
  resetLink: string,
  t: ReturnType<typeof useTranslations>,
): PasswordResetMailTemplate {
  return {
    subject: t('forgotPassword.mailSubject'),
    body: [
      '<html>',
      '<body>',
      `<p>${t('forgotPassword.mailIntro')}</p>`,
      `<p><a href="${resetLink}">${t('forgotPassword.mailLinkLabel')}</a></p>`,
      `<p>${t('forgotPassword.mailOutro')}</p>`,
      '</body>',
      '</html>',
    ].join('\n'),
  };
}

export function ForgotPasswordPage({ auth }: ForgotPasswordPageProps) {
  const t = useTranslations('auth');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');
  const [api] = useState(() => auth ?? createPortalApi().auth);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiErrorKey | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ defaultValues: { email: '' } });

  async function onSubmit(values: ForgotPasswordFormValues): Promise<void> {
    const email = values.email.trim();
    setSubmitting(true);
    setFailure(null);

    try {
      await api.requestPasswordReset(email, buildMailTemplate(buildResetLink(email), t));
      setSucceeded(true);
    } catch (error) {
      setFailure(errorKeyOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageContainer className="md-auth md-forgot-password-page flex min-h-full items-start justify-center px-6 py-12 max-sm:w-full max-sm:px-0 max-sm:py-4">
      <Card
        title={t('forgotPassword.title')}
        className="max-sm:box-border max-sm:w-full max-sm:p-4 max-sm:[&_p]:wrap-anywhere"
      >
        <p className="md-auth__subtitle">{t('forgotPassword.subtitle')}</p>

        {succeeded ? (
          <>
            <Alert variant="success">
              <strong>{t('forgotPassword.successTitle')}</strong>
              <p>{t('forgotPassword.successMessage')}</p>
            </Alert>
            <p className="md-auth__link">
              <Link to="/login">{t('forgotPassword.loginLink')}</Link>
            </p>
          </>
        ) : (
          <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {failure ? <Alert variant="error">{tErrors(failure)}</Alert> : null}

            <TextField
              label={t('forgotPassword.emailLabel')}
              type="email"
              autoComplete="email"
              invalid={Boolean(errors.email)}
              {...(errors.email?.message ? { message: errors.email.message } : {})}
              {...register('email', {
                required: t('forgotPassword.errors.emailRequired'),
                pattern: { value: EMAIL_PATTERN, message: t('forgotPassword.errors.emailInvalid') },
              })}
            />

            <Button
              type="submit"
              className="max-sm:w-full"
              loading={submitting}
              loadingLabel={tStatus('working')}
            >
              {t('forgotPassword.submitButton')}
            </Button>

            <p className="md-auth__link">
              <Link to="/login">{t('forgotPassword.loginLink')}</Link>
            </p>
          </form>
        )}
      </Card>
    </PageContainer>
  );
}
